import { deviceDistributionMethods } from "@/model/Prescription"
import { DAY_OF_WEEK_VALUES } from "@/model/Reminders"
import { supportalTimeZones } from "@/utils/timezoneOptions"
import { LocalDate } from "@js-joda/core"
import { z } from "zod"

const usPhoneNumberRegex = /^\d{10}$/ // Validate that the number has exactly 10 digits
const bloomlifeIdRegex = /^[0-9A-F]{32}$/ // Validate that the number is a valid UUID without dashes

const isLocalDate = (date: string): boolean => {
  try {
    LocalDate.parse(date)
    return true
  } catch {
    return false
  }
}

const isDateInFuture = (date: LocalDate): boolean => {
  const today = LocalDate.now()
  return date.isAfter(today)
}

export const medicalRecordNumber = z.string().trim()

export const bloomlifeId = z
  .string()
  .trim()
  .regex(bloomlifeIdRegex, "Bloomlife ID must be a valid UUID without dashes")

export const firstName = z.string()
export const lastName = z.string()

export const localDate = z
  .string()
  .refine(isLocalDate, { error: "Date is not valid", abort: true })

export const emailAddress = z.email("Please enter a valid email")

export const phoneNumber = z
  .string()
  .transform((val) => val.replace(/\D/g, ""))
  .pipe(
    z
      .string()
      .regex(
        usPhoneNumberRegex,
        "Please enter a valid 10-digit US phone number"
      )
  )

export const dateOfBirth = localDate.refine(
  (val) => !isDateInFuture(LocalDate.parse(val)),
  "Date of birth can't be in the future"
)

export const preferredLocale = z.string()

export const timeZone = z.enum(supportalTimeZones)

export const shippingAddress = z.object({
  address1: z.string().optional(),
  address2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),
})

export const insuranceProvider = z.string()

export const clinicalProtocolIndex = z.number().min(0)

export const diagnosis = z.string()

export const deviceDistributionMethod = z.enum(deviceDistributionMethods, {
  error: "Please select an option",
})

export const caregiverId = z.string()
export const departmentId = z.string()
export const patientOptedInClinically = z.enum(["yes", "no"])

export const cuffSize = z.enum(["S", "M", "L", "XL", "XXL", "PENDING"])

export const loginSchema = z.object({
  email: z.string().min(1, "Please enter your email address").email({
    error: "Please enter a valid email",
  }),
  password: z.string().min(1, "Please enter your password"),
})

export const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Please enter your email address").email({
    error: "Please enter a valid email",
  }),
})

const bioTPasswordRegex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@!%*#?&])[a-zA-Z\d$@!%*#?&\-_]{8,}$/

export const updatePasswordSchema = z
  .object({
    password: z
      .string()
      .min(1, "Please enter your password")
      .refine((password) => bioTPasswordRegex.test(password), {
        error: "error", // This message is not displayed
      }),
    passwordConfirmation: z.string().min(1, "Please confirm your password"),
  })
  .refine(
    ({ password, passwordConfirmation }) => password === passwordConfirmation,
    {
      error: "Passwords do not match",
      path: ["passwordConfirmation"],
    }
  )

export const addDeviceFormSchema = z.discriminatedUnion("identifierType", [
  z.object({
    identifierType: z.literal("imei"),
    imei: z.string().trim().min(1, "IMEI is a required field"),
  }),
  z.object({
    identifierType: z.literal("kit-id"),
    vendor: z.literal("impilo", {
      error: "Vendor is required",
    }),
    kitId: z.string().trim().min(1, "Kit ID is a required field"),
  }),
])

export const reminderSchema = z.object({
  time: z.string({
    error: "Time is required. Please select a time.",
  }),
  days: z.array(z.enum(DAY_OF_WEEK_VALUES)).refine((data) => data.length > 0, {
    error: "At least one day must be selected.",
  }),
  type: z.enum(["measurement", "medication"]),
})
